import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { authHeaders } from "@/lib/authToken";

type LensRow = {
  gccInstructions: string;
  faithInstructions: string;
  gccEnabled: boolean;
  faithEnabled: boolean;
  personalDnaEnabled: boolean;
  sourceOfTruthEnabled: boolean;
};

export function LibrarianLensesCard() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<LensRow | null>(null);

  const query = useQuery({
    queryKey: ["librarian-lenses"],
    queryFn: async () => {
      const headers = await authHeaders();
      const res = await fetch("/api/v1/librarian/lenses", { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error?.message ?? "Failed to load lenses");
      return body as LensRow;
    },
  });

  useEffect(() => {
    if (query.data) setDraft(query.data);
  }, [query.data]);

  const save = useMutation({
    mutationFn: async (row: LensRow) => {
      const headers = await authHeaders();
      const res = await fetch("/api/v1/librarian/lenses", {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(row),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error?.message ?? "Save failed");
      return body as LensRow;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["librarian-lenses"] });
      toast({ title: "Lenses saved" });
    },
    onError: (e: Error) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const row = draft ?? query.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Filter instructions (DNA / Faith / GCC)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          These are short riders the Librarian may attach when relevant. They do not dump your whole identity into every Claude request.
        </p>
        {query.isLoading || !row ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <label className="flex items-center justify-between">
              <span className="text-sm">Source of Truth pointers</span>
              <Switch
                checked={row.sourceOfTruthEnabled}
                onCheckedChange={(v) => setDraft({ ...row, sourceOfTruthEnabled: v })}
              />
            </label>
            <label className="flex items-center justify-between">
              <span className="text-sm">Personal DNA slice</span>
              <Switch
                checked={row.personalDnaEnabled}
                onCheckedChange={(v) => setDraft({ ...row, personalDnaEnabled: v })}
              />
            </label>
            <label className="flex items-center justify-between">
              <span className="text-sm">Faith lens</span>
              <Switch checked={row.faithEnabled} onCheckedChange={(v) => setDraft({ ...row, faithEnabled: v })} />
            </label>
            <div className="space-y-1">
              <Label>Faith instructions</Label>
              <Textarea
                rows={3}
                value={row.faithInstructions}
                onChange={(e) => setDraft({ ...row, faithInstructions: e.target.value })}
                placeholder="Short faith/worldview constraints for retrieval…"
              />
            </div>
            <label className="flex items-center justify-between">
              <span className="text-sm">GCC lens</span>
              <Switch checked={row.gccEnabled} onCheckedChange={(v) => setDraft({ ...row, gccEnabled: v })} />
            </label>
            <div className="space-y-1">
              <Label>GCC instructions</Label>
              <Textarea
                rows={3}
                value={row.gccInstructions}
                onChange={(e) => setDraft({ ...row, gccInstructions: e.target.value })}
                placeholder="Region/culture/business rules the Librarian should apply as a filter…"
              />
            </div>
            <Button size="sm" onClick={() => save.mutate(row)} disabled={save.isPending}>
              Save lenses
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
